import { HStack, Stack, Text } from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "@/components/atoms/Button"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"
import type { UserWithRoles } from "@/types/models"
import { Input } from "@chakra-ui/react"

import { Checkbox } from "@/components/atoms/Checkbox"
import { FormField } from "@/components/molecules/FormField"
import { Field } from "@chakra-ui/react"
const editUserSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  full_name: z
    .string()
    .min(1, "Full name is required")
    .max(100, "Full name must be less than 100 characters"),
  is_active: z.boolean(),
  email_verified: z.boolean(),
})

type EditUserFormData = z.infer<typeof editUserSchema>

export interface EditUserFormProps {
  user: UserWithRoles
  onSuccess?: () => void
  onCancel?: () => void
}

export const EditUserForm = ({
  user,
  onSuccess,
  onCancel,
}: EditUserFormProps) => {
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      email: user.email,
      full_name: user.full_name || "",
      is_active: user.is_active,
      email_verified: user.email_verified || false,
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: async (data: EditUserFormData) => {
      const response = await api.admin.updateUserApiV1AdminUsersUserIdPut({
        userId: user.id,
        requestBody: {
          email: data.email,
          full_name: data.full_name,
          is_active: data.is_active,
        },
      })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast("User updated successfully")
      onSuccess?.()
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to update user: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const onSubmit = (data: EditUserFormData) => {
    updateUserMutation.mutate(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        {/* Basic Information */}
        <Stack gap={4}>
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            Basic Information
          </Text>

          {/* Full Name */}
          <FormField
            label="Full Name"
            required
            isInvalid={!!errors.full_name}
            errorText={errors.full_name?.message}
          >
            <Input {...register("full_name")} placeholder="Enter full name" />
          </FormField>

          {/* Email */}
          <FormField
            label="Email Address"
            required
            isInvalid={!!errors.email}
            errorText={errors.email?.message}
          >
            <Input
              {...register("email")}
              type="email"
              placeholder="Enter email address"
            />
          </FormField>
        </Stack>

        {/* Account Status */}
        <Stack gap={4}>
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            Account Status
          </Text>

          <Stack gap={3}>
            {/* Active Status */}
            <Field.Root>
              <Checkbox {...register("is_active")} checked={user.is_active}>
                <Stack gap={1}>
                  <Text fontSize="sm" fontWeight="medium">
                    Active Account
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Allow the user to sign in and access the application
                  </Text>
                </Stack>
              </Checkbox>
            </Field.Root>

            {/* Verified Status */}
            <Field.Root>
              <Checkbox
                {...register("email_verified")}
                checked={user.email_verified || false}
              >
                <Stack gap={1}>
                  <Text fontSize="sm" fontWeight="medium">
                    Email Verified
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Mark the user's email address as verified
                  </Text>
                </Stack>
              </Checkbox>
            </Field.Root>
          </Stack>
        </Stack>

        {/* User ID (Read-only) */}
        <FormField label="User ID" helperText="This field cannot be changed">
          <Input value={user.id} readOnly bg="gray.50" cursor="not-allowed" />
        </FormField>

        {/* Action Buttons */}
        <HStack justify="end" gap={3} pt={4}>
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            loading={isSubmitting}
            disabled={!isDirty}
          >
            Update User
          </Button>
        </HStack>
      </Stack>
    </form>
  )
}

export default EditUserForm
